import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getProduct } from '@dukanti/shared';
import type { ProductWithVariants } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { productKeys } from './use-products';

/**
 * Hook: fetch a single product by id with its images. Returns `null`
 * when the product is not found so the caller can render a "not found"
 * state without treating it as an error.
 *
 * Disabled when `productId` is undefined (route param not yet resolved).
 */
export function useProduct(
  productId: string | undefined,
): UseQueryResult<ProductWithVariants | null> {
  return useQuery({
    queryKey: productId ? productKeys.detail(productId) : productKeys.all,
    enabled: !!productId,
    staleTime: 30_000,
    queryFn: async () => {
      // Unreachable defensive guard: React Query never invokes queryFn
      // while `enabled: !!productId` is false, so the `!productId`
      // branch can't be hit via the hook's public contract.
      /* v8 ignore next 3 */
      if (!productId) {
        throw new Error('unreachable: enabled guard should prevent this');
      }
      return getProduct(supabase, productId);
    },
  });
}
